import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationActionStatus,
  AutomationStatus,
  EventChannel,
  ExperienceActorType,
  ExperienceEventType,
  IExperience,
  IExperienceEvent,
  IExperienceTemplate,
  IInstanceFieldOverride,
  MessageStatus,
  SubTaskStatus,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import {
  createFieldOverride,
  getActorOverrideKey,
  getAnchorOverrideKey
} from '@dadohr/shared/domainTypes/instanceFieldOverride';
import { fromPairs, isEqual, keyBy, uniq } from 'lodash';
import shortid from 'shortid';
import { getActorForKey } from '../../../../services/experiences/instance';
import { refreshTimestamp } from '../../../../services/firebase';
import { Timestamp } from '../../../../services/time';

export type InstanceModifications = {
  tasks: {
    [taskId: string]: {
      changed: boolean;
      subTasks: {
        [subTaskId: string]: {
          changed: boolean;
        };
      };
    };
  };
  messages: {
    [msgId: string]: {
      changed: boolean;
    };
  };
  automations: {
    [automationId: string]: {
      changed: boolean;
      actions: {
        [actionId: string]: {
          changed: boolean;
        };
      };
    };
  };
};

export const getModifications = (
  bef: Doc<IExperience>,
  aft: Doc<IExperience>
): InstanceModifications => {
  const res: InstanceModifications = {
    tasks: {},
    automations: {},
    messages: {}
  };

  const befTasksById = keyBy(bef.data.tasks, (t) => t.id);
  const aftTasksById = keyBy(aft.data.tasks, (t) => t.id);
  const allTaskIds = uniq([
    ...Object.keys(befTasksById),
    ...Object.keys(aftTasksById)
  ]);
  const allMsgIds = uniq([
    ...Object.keys(bef.data.messagesStatuses),
    ...Object.keys(aft.data.messagesStatuses)
  ]);
  const allAutomationIds = uniq([
    ...Object.keys(bef.data.automations),
    ...Object.keys(aft.data.automations)
  ]);

  allTaskIds.forEach((taskId) => {
    const befTask = befTasksById[taskId];
    const aftTask = aftTasksById[taskId];
    if (!aftTask) {
      return;
    }
    if (!befTask) {
      res.tasks[taskId] = {
        changed: true,
        subTasks: fromPairs(
          aftTask.subTasks.map((s) => [s.id, { changed: true }])
        )
      };
      return;
    }
    // simple equality should work here, if all other code plays nicely - but as it's likely
    // that some piece doesn't, let's take no risks and try deep equality checking
    if (isEqual(befTask, aftTask)) {
      return;
    }
    res.tasks[taskId] = { changed: true, subTasks: {} };

    const befSubTasksById = keyBy(befTask.subTasks, (s) => s.id);
    const aftSubTasksById = keyBy(aftTask.subTasks, (s) => s.id);

    const allSubTaskIds = [
      ...Object.keys(befSubTasksById),
      ...Object.keys(aftSubTasksById)
    ];
    allSubTaskIds.forEach((subTaskId) => {
      const befSubTask = befSubTasksById[subTaskId];
      const aftSubTask = aftSubTasksById[subTaskId];
      if (!isEqual(befSubTask, aftSubTask)) {
        res.tasks[taskId].subTasks[subTaskId] = { changed: true };
      }
    });
  });

  allAutomationIds.forEach((autId) => {
    const befAut = bef.data.automations[autId];
    const aftAut = aft.data.automations[autId];
    // simple equality should work here, if all other code plays nicely - but as it's likely
    // that some piece doesn't, let's take no risks and try deep equality checking
    if (isEqual(befAut, aftAut)) {
      return;
    }
    res.automations[autId] = { changed: true, actions: {} };

    // Be defensive - especially befAut might not be defined, as a new automation could have popped up
    const allActionIds = [
      ...Object.keys(befAut?.actions || {}),
      ...Object.keys(aftAut?.actions || {})
    ];
    allActionIds.forEach((actionId) => {
      const befAct = befAut?.actions[actionId];
      const aftAct = aftAut?.actions[actionId];
      if (!isEqual(befAct, aftAct)) {
        res.automations[autId].actions[actionId] = { changed: true };
      }
    });
  });

  allMsgIds.forEach((msgId) => {
    const befMsg = bef.data.messagesStatuses[msgId];
    const aftMsg = aft.data.messagesStatuses[msgId];
    // simple equality should work here, if all other code plays nicely - but as it's likely
    // that some piece doesn't, let's take no risks and try deep equality checking
    if (!isEqual(befMsg, aftMsg)) {
      res.messages[msgId] = { changed: true };
    }
  });

  return res;
};

export const getChangeEvents = (
  tpl: Doc<IExperienceTemplate>,
  originalD: Doc<IExperience>,
  d: Doc<IExperience>,
  currenUserId: string,
  at: Timestamp
): IExperienceEvent[] => {
  // This is going to be a rather complex function, as we do not want to record certain
  // things:
  // - When an anchor is moved a lot of items move - those are irrelevant
  // - when a full task is skipped, then all subtasks are skipped -> record that the full
  //   task got skipped instead

  const events: IExperienceEvent[] = [];

  const befTasksById = keyBy(originalD.data.tasks, (t) => t.id);

  const common = {
    initiator: currenUserId,
    at,
    channel: EventChannel.ADMIN_APP
  };

  // UNCOMPLETES
  d.data.tasks.forEach((t) => {
    const ot = befTasksById[t.id];
    if (!ot) {
      return;
    }
    const befSubTasksById = keyBy(ot.subTasks, (s) => s.id);
    t.subTasks.forEach((s) => {
      const os = befSubTasksById[s.id];
      if (!os) {
        return;
      }

      if (s.status === SubTaskStatus.OPEN && os.status === SubTaskStatus.DONE) {
        events.push({
          ...common,
          type: ExperienceEventType.SUB_TASK_UNDONE,
          id: shortid(),
          taskId: t.id,
          subTaskId: s.id
        });
      }
    });
  });

  // TODO - collect preskips!
  // SKIPS
  d.data.tasks.forEach((t) => {
    const ot = befTasksById[t.id];
    if (!ot) {
      return;
    }
    if (t.status === TaskStatus.SKIPPED && ot.status !== TaskStatus.SKIPPED) {
      events.push({
        ...common,
        type: ExperienceEventType.TASK_SKIPPED,
        id: shortid(),
        taskId: t.id
      });
      // in this case no need to check for individual subtasks anymore
    } else if (
      t.status !== TaskStatus.SKIPPED &&
      ot.status === TaskStatus.SKIPPED
    ) {
      events.push({
        ...common,
        type: ExperienceEventType.TASK_UNSKIPPED,
        id: shortid(),
        taskId: t.id
      });
      // in this case no need to check for individual subtasks anymore
    } else {
      const befSubTasksById = keyBy(ot.subTasks, (s) => s.id);
      t.subTasks.forEach((s) => {
        const os = befSubTasksById[s.id];
        if (!os) {
          return;
        }
        if (
          s.status === SubTaskStatus.SKIPPED &&
          os.status !== SubTaskStatus.SKIPPED
        ) {
          events.push({
            ...common,
            type: ExperienceEventType.SUB_TASK_SKIPPED,
            id: shortid(),
            taskId: t.id,
            subTaskId: s.id
          });
        }
        if (
          s.status !== SubTaskStatus.SKIPPED &&
          os.status === SubTaskStatus.SKIPPED
        ) {
          events.push({
            ...common,
            type: ExperienceEventType.SUB_TASK_UNSKIPPED,
            id: shortid(),
            taskId: t.id,
            subTaskId: s.id
          });
        }
      });
    }
  });

  Object.values(d.data.automations).forEach((a) => {
    const oa = originalD.data.automations[a.templateId];
    if (!oa) {
      return;
    }

    if (
      a.status === AutomationStatus.SKIPPED &&
      oa.status !== AutomationStatus.SKIPPED
    ) {
      events.push({
        ...common,
        type: ExperienceEventType.AUTOMATION_SKIPPED,
        id: shortid(),
        automationTplId: a.templateId
      });
      // in this case no need to check for individual actions anymore
    } else if (
      a.status !== AutomationStatus.SKIPPED &&
      oa.status === AutomationStatus.SKIPPED
    ) {
      events.push({
        ...common,
        type: ExperienceEventType.AUTOMATION_UNSKIPPED,
        id: shortid(),
        automationTplId: a.templateId
      });
      // in this case no need to check for individual actions anymore
    } else {
      Object.values(a.actions).forEach((ac) => {
        const oac = oa.actions[ac.templateId];
        if (!oac) {
          return;
        }
        if (
          ac.status === AutomationActionStatus.SKIPPED &&
          oac.status !== AutomationActionStatus.SKIPPED
        ) {
          events.push({
            ...common,
            type: ExperienceEventType.AUTOMATION_ACTION_SKIPPED,
            id: shortid(),
            automationTplId: a.templateId,
            acionTplId: ac.templateId
          });
        }
        if (
          ac.status !== AutomationActionStatus.SKIPPED &&
          oac.status === AutomationActionStatus.SKIPPED
        ) {
          events.push({
            ...common,
            type: ExperienceEventType.AUTOMATION_ACTION_UNSKIPPED,
            id: shortid(),
            automationTplId: a.templateId,
            acionTplId: ac.templateId
          });
        }
      });
    }
  });

  Object.entries(d.data.messagesStatuses).forEach(([msgId, m]) => {
    const om = originalD.data.messagesStatuses[msgId];
    if (!om) {
      return;
    }

    if (
      m.status === MessageStatus.SKIPPED &&
      om.status !== MessageStatus.SKIPPED
    ) {
      events.push({
        ...common,
        type: ExperienceEventType.MESSAGE_SKIPPED,
        id: shortid(),
        msgId
      });
    } else if (
      m.status !== MessageStatus.SKIPPED &&
      om.status === MessageStatus.SKIPPED
    ) {
      events.push({
        ...common,
        type: ExperienceEventType.MESSAGE_UNSKIPPED,
        id: shortid(),
        msgId
      });
    }
  });

  Object.values(tpl.data.supportingActors).forEach((ac) => {
    const prevActor = getActorForKey(originalD, ac.key);
    const nextActor = getActorForKey(d, ac.key);

    // TODO Slack channel - might not have a user id here but rather a channel... We shall see

    const prevActorId =
      prevActor?.type !== ExperienceActorType.CHANNEL
        ? prevActor?.userId || ''
        : '';
    const nextActorId =
      nextActor?.type !== ExperienceActorType.CHANNEL
        ? nextActor?.userId || ''
        : '';

    // There is currently no way to REMOVE a supporting actor
    if (prevActorId !== nextActorId && nextActorId) {
      events.push({
        ...common,
        type: ExperienceEventType.SUPPORTING_ACTOR_CHANGED,
        id: shortid(),
        actorKey: ac.key,
        executedBy: 'user',
        prev: prevActorId || null,
        next: nextActorId
      });
    }
  });

  // `refreshTimestamp` re-hydrates `_seconds/_nanoseconds` plain objects back into
  // real Firebase Timestamps. Needed because the manual-enroll modal does a
  // `cloneDeep(originalInstances)` to build `editableInstances`, which strips the
  // Timestamp prototype on the anchor's `at`. Without re-hydration, the next
  // value's `seconds`/`nanoseconds` getters are gone and `prevAnchor.isEqual(nextAnchor)`
  // reads them as `undefined` → returns false → falsely emits ANCHOR_CHANGED
  // (and, in getFieldOverridesFromChanges below, falsely records anchor_date
  // as manually overridden — breaking subsequent user-profile imports).
  const prevAnchor = refreshTimestamp(originalD.data.anchorDate.at);
  const nextAnchor = refreshTimestamp(d.data.anchorDate.at);
  if (!prevAnchor.isEqual(nextAnchor)) {
    events.push({
      ...common,
      type: ExperienceEventType.ANCHOR_CHANGED,
      id: shortid(),
      executedBy: 'user',
      prev: prevAnchor,
      next: nextAnchor
    });
  } else {
    // only check for move events if the anchor is stable. otherwise it might be an anchor move event
    // which also moves timeline items
    // This is slightly lossy, as we're not identifying if someone moves something together with an anchor
    // We COULD try to see if something turned to "manual overwrite" in addition - as this is an indicator
    // of such a move.
    d.data.tasks.forEach((t) => {
      const ot = befTasksById[t.id];
      if (!ot) {
        return;
      }

      if (!t.timing.start.isEqual(ot.timing.start)) {
        events.push({
          ...common,
          type: ExperienceEventType.TASK_MOVED,
          id: shortid(),
          tTId: t.templateId,
          prevStart: ot.timing.start,
          nextStart: t.timing.start
        });
      }
    });

    Object.values(d.data.automations).forEach((a) => {
      const oa = originalD.data.automations[a.templateId];
      if (!oa) {
        return;
      }

      if (!a.timing.at.isEqual(oa.timing.at)) {
        events.push({
          ...common,
          type: ExperienceEventType.AUTOMATION_MOVED,
          id: shortid(),
          automationTplId: a.templateId,
          prevStart: oa.timing.at,
          nextStart: a.timing.at
        });
      }
    });

    Object.entries(d.data.messagesStatuses).forEach(([msgId, m]) => {
      const om = originalD.data.messagesStatuses[msgId];
      if (!om) {
        return;
      }

      if (!m.timing.at.isEqual(om.timing.at)) {
        events.push({
          ...common,
          type: ExperienceEventType.MESSAGE_MOVED,
          id: shortid(),
          msgId,
          prevStart: om.timing.at,
          nextStart: m.timing.at
        });
      }
    });
  }

  return events;
};

/**
 * Computes field override records for manual actor/anchor changes.
 * Merges with existing overrides so only changed fields are updated.
 */
export const getFieldOverridesFromChanges = (
  tpl: Doc<IExperienceTemplate>,
  originalD: Doc<IExperience>,
  d: Doc<IExperience>,
  currentUserId: string,
  n: Timestamp
): { [fieldKey: string]: IInstanceFieldOverride } => {
  const result: { [fieldKey: string]: IInstanceFieldOverride } = {
    ...(d.data.fieldOverrides || {})
  };

  Object.values(tpl.data.supportingActors).forEach((ac) => {
    const prevActor = getActorForKey(originalD, ac.key);
    const nextActor = getActorForKey(d, ac.key);

    const prevActorId =
      prevActor?.type !== ExperienceActorType.CHANNEL
        ? prevActor?.userId || ''
        : '';
    const nextActorId =
      nextActor?.type !== ExperienceActorType.CHANNEL
        ? nextActor?.userId || ''
        : '';

    if (prevActorId !== nextActorId) {
      result[getActorOverrideKey(ac.key)] = createFieldOverride(currentUserId, n);
    }
  });

  // Re-hydrate Timestamps for comparison — see getChangeEvents above. Without
  // this, cloneDeep'd next-anchor returns false from isEqual and we record a
  // spurious anchor_date override even though the admin didn't change anything,
  // which causes subsequent user-profile imports to be rejected as
  // "Manually overridden dates are not updated by imports".
  const prevAnchor = refreshTimestamp(originalD.data.anchorDate.at);
  const nextAnchor = refreshTimestamp(d.data.anchorDate.at);
  if (!prevAnchor.isEqual(nextAnchor)) {
    result[getAnchorOverrideKey()] = createFieldOverride(currentUserId, n);
  }

  return result;
};
